import React, { Fragment, useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { motion, AnimatePresence } from 'framer-motion'
import dayjs from 'dayjs'
import theme from '../../theme'

import I18n, { Trans } from '../../i18n'

import Card from '../../components/Card'
import ConditionalWrapper from '../../components/ConditionalWrapper'
import { CityScape as CityIcon } from '../../components/Icon'
import * as Icon from '../../components/Icon'
import RankingItem from '../../components/RankingItem'
import Section from '../../components/Section'
import MultiSelectNative from '../../components/native/MultiSelectNative'
import SingleSelect from '../../components/forms/SingleSelect'
import {
  findActiveTimerangeItem,
  getTimerangeSelectorItems
} from '../../components/native/predefinedFiltersNative'
import { transformLocationsToColoredLocationSelectorOptions } from '../../lib/transformers/location-transformers'
import useModalState, { ModalStateProvider } from '../../lib/use-modal-state'
import { CsrfContext } from '../../components/forms/Form'

import LoadingIndicator from '../../components/Graph/LoadingIndicator'

const getInitialLineChartColors = () => {
  const colors = []
  theme.chroma.KPICharts.map(kpiChartColorProfile => {
    colors.push(kpiChartColorProfile.fill.hex())
    colors.push(kpiChartColorProfile.fillComplement.hex())
  })
  return colors
}

function transformLocationsToSelectableOptions(locations, chartColors) {
  const locationsAsSelectorOptions = transformLocationsToColoredLocationSelectorOptions(
    locations,
    chartColors
  )

  return locationsAsSelectorOptions
}

function LocationsCompareNativeInner({
  topLocations,
  locations,
  loggedIn,
  showMaintenanceBanner,
  permissions,
  favLocationIds
}) {
  const [_activeModal] = useModalState()
  const [selectableLocations, setSelectableLocations] = useState([])
  const [selectedLocations, setSelectedLocations] = useState([])
  const [selectedLocationsToShow, setSelectedLocationsToShow] = useState([])
  const [selectableTimerange, setSelectableTimerange] = useState([])
  const [isLoading, setIsLoading] = useState([])
  const [multiLocationChartColors, _setMultiLocationChartColors] = useState(
    getInitialLineChartColors()
  )
  const csrfToken = useContext(CsrfContext)
  const [timerange, setTimerange] = useState()

  const initLocalStorage = () => {
    let existing = localStorage.getItem('compare-history')
    if(!existing){
      localStorage.setItem('compare-history', JSON.stringify([]))
      return false
    }
    return existing
  }

  const addSettingToHistory = (location_ids, timerange) => {
    let existing = localStorage.getItem('compare-history')
    try{
      existing = JSON.parse(existing)
    } catch(e) {
      console.error(e)
    }
    
    existing = Array.isArray(existing) ? existing : []

    existing.unshift({locations: location_ids, timerange: timerange})
    localStorage.setItem('compare-history', JSON.stringify(existing.slice(0,5)))
  }

  const formatSavedTimeframeToCurrentDate = (timerange, selected) => {
    let transformed_timerange = null
    
    if (timerange.label.includes("Heute") || timerange.label.includes("Today")) {
      transformed_timerange = {
        from: dayjs().startOf('day').toDate(),
        to: dayjs().endOf('day').toDate()
      };
    } else if (timerange.label.includes("Gestern") || timerange.label.includes("Yesterday")) {
      transformed_timerange = {
        from: dayjs().subtract(1, 'day').startOf('day').toDate(),
        to: dayjs().subtract(1, 'day').endOf('day').toDate()
      };
    } else if (timerange.label.includes("Letzter Samstag") || timerange.label.includes("Last Saturday")) {
      transformed_timerange = {
        // weekday(-2) gets the last saturday in dayjs.
        from: dayjs().weekday(-2).startOf('day').toDate(),
        to: dayjs().weekday(-2).endOf('day').toDate()
      };
    }
    

    if (transformed_timerange) {
        return findActiveTimerangeItem(
        transformed_timerange,
        getTimerangeSelectorItems(null, null, permissions, 
          topLocations
          .filter(l => selected.some(s => s.value.id === l.locationId))
          .sort((a, b) => b.totalPedestrians - a.totalPedestrians))
      )
    } else {
      return {
        ...timerange,
        label: timerange.label || 'Custom',
        value: transformed_timerange ?? {
          from: new Date(timerange.from),
          to: new Date(timerange.to)
        },
        icon: <Icon.FA icon={Icon.FaCalendar} />
      }
    }
  }
  
  const initializeSelectedLocations = (selectableLocations) => {
    let selected = [];
    let selectedToShow = [];
    let lastSettings = initLocalStorage();
    let initialTimerange = null;

    if (lastSettings) {
      // if present in localStorage, get the last filter input and set it
      lastSettings = JSON.parse(lastSettings);
      selected = selectableLocations.filter(l =>
        lastSettings[0].locations.some(t => t == l.value.id)
      );
      initialTimerange = formatSavedTimeframeToCurrentDate(lastSettings[0].timerange, selected);
    } else if (favLocationIds.length > 0) {
      // fallback 1: select the users favorites
      selected = selectableLocations.filter(l => favLocationIds.some(t => t == l.value.id));
    } else {
      // fallback 2: if user has no favorites, select the top 5 locations
      let topIds = topLocations
        .sort((a, b) => b.pedestriansCount - a.pedestriansCount)
        .slice(0, 5)
        .map(l => l.locationId);
      selected = selectableLocations.filter(l =>
        topIds.some(top => top == l.value.id)
      );
    }

    selectedToShow = topLocations
      .filter(l => selected.some(s => s.value.id == l.locationId))
      .sort((a, b) => b.totalPedestrians - a.totalPedestrians);
    setSelectedLocations(selected)

    initialTimerange = initialTimerange ?? findActiveTimerangeItem(
      {from: dayjs()},
      getTimerangeSelectorItems(null, null, permissions, selectedToShow)
    )

    setSelectableTimerange(getTimerangeSelectorItems(null, null, permissions, selectedToShow))
    setTimerange(initialTimerange) // this triggers fetchData() through useEffect() so we do not need to call it here to init data
  }

  function handleLocationSelect(newSelectedLocations, triggeredAction) {
    const selectedLocationIds =
      newSelectedLocations && newSelectedLocations.length > 0
        ? newSelectedLocations.map(selectedLocation => {
            return selectedLocation.value.id
          })
        : []

    setSelectedLocations(newSelectedLocations)
    setSelectedLocationsToShow(topLocations.filter(l => selectedLocationIds.some(s => s == l.locationId)).sort((a,b) => b.totalPedestrians - a.totalPedestrians))
  }

  function handleTimerangeChange(newTimerange) {
    setTimerange(newTimerange)
  }

  function fetchData(locationIds, timerange) {
    setIsLoading(true)
    fetch('/locations/pedestrian_sums', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ locations: locationIds, from: timerange.from, to: timerange.to })
    })
      .then(res => res.json())
      .then(data => {
        setSelectedLocationsToShow(data)
        setIsLoading(false)
      })
  }

  useEffect(() => {
    if(selectedLocations && timerange) {
      addSettingToHistory(selectedLocations.map(l => l.value.id), timerange)
      fetchData(selectedLocations.map(l => l.value.id), timerange)
    }
  }, [selectedLocations, timerange])

  useEffect(() => {
     if (!locations) return
      const locationsCopy = locations
      const newSelectableLocations = transformLocationsToSelectableOptions(
        locationsCopy,
        multiLocationChartColors
      )
  
      setSelectableLocations(newSelectableLocations.sort((a,b) => {return a.label.localeCompare(b.label)}))
      initializeSelectedLocations(newSelectableLocations)
  }, [])

  return (
    <div css={{ minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
      <MainSection mid>
        <Section.Wrapper>
          {!showMaintenanceBanner && 
          <>
            <MultiSelectContainer>
                <MultiSelectNative
                  css={{ zIndex: '1', width: '100%' }}
                  nativeAppStyles={true}
                  options={selectableLocations}
                  placeholder={I18n.t(
                    'location.compare_multiple_locations.choose_locations_to_compare'
                  )}
                  onChange={handleLocationSelect}
                  value={selectedLocations}
                  limit={10}
                  closeMenuOnSelect={false}
                  enableColouredOptions
                  optionColors={multiLocationChartColors}
                  defaultValue={selectedLocations}
                  enableOptionAbbreviations={false}
                />
              </MultiSelectContainer>
              <SelectWrapper data-test-id="timerange_selector">
                <SingleSelect
                    options={selectableTimerange}
                    onChange={handleTimerangeChange}
                    value={timerange}
                    variant={'SECONDARY'}
                  />
              </SelectWrapper>
        
            {selectedLocations.length === 0 && !isLoading && (
              <NoLocationsYet>
                <CityIcon width={90} height={90} />
                <Trans id="comparison.no_locations" copy />
              </NoLocationsYet>
            )}

            {selectedLocations.length > 0 && selectedLocationsToShow.length === 0 && !isLoading && (
              <NoLocationsYet>
                <CityIcon width={90} height={90} />
                <Trans id="comparison.no_data" copy />
              </NoLocationsYet>
            )}

            {isLoading && (
              <LoadingWrapper>
                <LoadingIndicator active={true}/>
              </LoadingWrapper>
            )}

            {selectedLocations.length > 0 && selectedLocationsToShow.length > 0 && !isLoading && (
              <RankingCard>
                <ol>
                  <AnimatePresence>
                    {selectedLocationsToShow.map((location, index) => (
                      <Fragment key={location.locationId}>
                        <ListItem
                          initial={index < 10 ? false : { x: -10, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                        >
                          <ConditionalWrapper
                            condition={loggedIn}
                            wrapper={children => (
                              <a href={`/locations/${location.locationId}`}>
                                {children}
                              </a>
                            )}
                          >
                            <RankingItem
                              rank={index + 1}
                              percentage={percentageFromMinMax({
                                min:
                                  0, // 0 is hard-coded here because otherwise, the scaling was off for locations with small pedestrian counts
                                max: selectedLocationsToShow[0].pedestriansCount,
                                value: location.pedestriansCount
                              })}
                              locationName={location.locationName}
                              cityName={location.cityName}
                              pedestriansCount={location.pedestriansCount}
                              animate={index >= 10}
                            />
                          </ConditionalWrapper>
                        </ListItem>
                      </Fragment>
                    ))}
                  </AnimatePresence>
                </ol>
              </RankingCard>
            )}
          </>
          }
        </Section.Wrapper>
      </MainSection>
    </div>
  )
}

LocationsCompareNativeInner.propTypes = {
  loggedIn: PropTypes.bool,
  locations: PropTypes.object.isRequired,
  topLocations: PropTypes.arrayOf(
    PropTypes.shape({
      locationId: PropTypes.number.isRequired,
      pedestriansCount: PropTypes.number.isRequired,
      locationName: PropTypes.string.isRequired,
      cityName: PropTypes.string.isRequired,
      locationShowMeasurementsFrom: PropTypes.instanceOf(Date).isRequired
    })
  ).isRequired,
  locationsCount: PropTypes.number.isRequired,
  showAdvertisingBanner: PropTypes.bool.isRequired,
  showMaintenanceBanner: PropTypes.bool.isRequired
}

const ListItem = styled(motion.li)`
  border-bottom: 0.125rem solid ${p => p.theme.chroma.nearlyWhite.css()};
  overflow: hidden;
  transform-origin: top;

  :last-of-type {
    border-bottom: none;
  }

  &:hover {
    cursor: pointer;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  pointer-events: none;
  padding-top: 2rem;
`

const MainSection = styled(Section)`
  padding-top: 2rem;
  padding-bottom: 5rem;
  flex: 1 0 auto;
`

const RankingCard = styled(Card)`
  padding: 0;
  margin-top: 2.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
`

const NoLocationsYet = styled.div`
  padding: 6rem;
  font-size: 1.5rem;
  font-style: italic;
  text-align: center;
  opacity: 0.5;

  p {
    margin-top: 1rem;
  }
`

function percentageFromMinMax({ min, max, value }) {
  return (value - min) / (max - min)
}

const MultiSelectContainer = styled.div`
  padding: 0;
  margin: 0;
  z-index: 3;
  margin-left: -1.25rem;
  margin-right: -1.25rem;
`

const SelectWrapper = styled.div`
  min-width: 16rem;
  padding-bottom: 0.9rem;
  margin-left: 0;
  height: 2.5rem;
  margin-left: -1.25rem;
  margin-right: -1.25rem;

  @media (min-width: 1142px) {
    margin-left: 1rem;
  }
`

function LocationsCompareNative(props) {
  return (
    <ModalStateProvider>
      <LocationsCompareNativeInner {...props} />
    </ModalStateProvider>
  )
}

export default LocationsCompareNative
